import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { navigate } from '@reach/router';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-toastify';
import SearchableSelect from '../common/SearchableSelect';
import ThemedConfirmDialog from '../common/ThemedConfirmDialog';
import AuditHistoryDialog from '../common/AuditHistoryDialog';
import { FeatureGlyph } from '../features/FeatureMarkers';
import MessagePreview from './MessagePreview';
import {
  buildSampleOrderSummaryHtml,
  buildSampleOrderSummaryText,
  parseSummaryConfig,
} from './orderSummary';
import { accountLogoImgHtml, parseBrandColors, stripPaymentContent } from './layoutCards';
import { SAMPLE_PHARMACY_HOURS_HTML, SAMPLE_KIOSK_HOURS_HTML, buildSampleQrHtml, formatExpiryInDays } from './tokens';
import { parseMobileConfig } from './mobileConfig';
import { idsEqual } from './lineage';
import {
  CUSTOM_BRAND_MESSAGING_KEY,
  EMAIL_TEMPLATES_PATH,
  LINK_MESSAGES_PATH,
  MESSAGE_CONTROL_PATH,
  SMS_TEMPLATES_PATH,
  STAFF_TEMPLATES_PATH,
  fetchFeatureLocations,
  getFeatureLockedRedirectPath,
  messagingMethodForLocationType,
} from '../../utils/productLicensing';
import {
  createListedTemplate,
  deleteListedTemplate,
  fetchMessageOverview,
  fetchTemplateAudit,
  fetchTemplateHtml,
  fetchTemplateList,
  setMessageTemplateDefault,
} from './api';
import {
  eventsForMethod,
  eventLabel,
  eventWhen,
  DEFAULT_EVENT,
  CUSTOM_EVENT_VALUE,
  slugifyEvent,
  isValidEventKey,
} from './events';
import { MessageWhenHelpButton } from './MessageWhenHelp';
import ILocalAGGrid, {
  getDefaultColDef,
} from '../grid/iLocalAGGrid';
import './message-control-center.css';
import './template-list.css';
import '../features/features.css';

const METHOD_LABELS = {
  counter: 'Counter',
  curbside: 'Curbside',
  bedside: 'Bedside',
  courier: 'Courier',
  kiosk: 'Kiosk / locker',
  mail_order: 'Mail Order',
  other: 'Other',
  account: 'Staff / account',
};

const locationTypeIdOf = (location) =>
  location && (location.location_type_id || location.locationtype_id);

const methodForLocation = (location, isStaffChannel) => {
  if (isStaffChannel) return 'account';
  return messagingMethodForLocationType(locationTypeIdOf(location)) || '';
};

const channelForNewTemplate = (isStaffChannel, listChannel) =>
  isStaffChannel ? 'email' : listChannel;

const takenEventKeysForLocation = ({
  rows,
  boxId,
  method,
  channel,
  isStaffChannel,
}) => {
  const expectedChannel = channelForNewTemplate(isStaffChannel, channel);
  const taken = new Set();
  (rows || []).forEach((row) => {
    if (!idsEqual(row.box_id, boxId)) return;
    if (method && row.delivery_method !== method) return;
    const rowChannel = row.channel || expectedChannel;
    if (rowChannel !== expectedChannel) return;
    taken.add(row.event_key || DEFAULT_EVENT);
  });
  return taken;
};

const firstEventFor = (method, channel, taken) => {
  const events = method ? eventsForMethod(method, channel) : [];
  const open = events.find((event) => !taken || !taken.has(event.key));
  return (open && open.key) || '';
};

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const editorPath = ({ boxId, method, channel, eventKey }) =>
  `${MESSAGE_CONTROL_PATH}?box_id=${encodeURIComponent(
    boxId || ''
  )}&type=${encodeURIComponent(method || '')}&event=${encodeURIComponent(
    eventKey || DEFAULT_EVENT
  )}&channel=${encodeURIComponent(channel || '')}&from=list`;

const compareUpdatedDay = (filterLocalDateAtMidnight, cellValue) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return -1;
  const cellDay = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate()
  );
  if (cellDay < filterLocalDateAtMidnight) return -1;
  if (cellDay > filterLocalDateAtMidnight) return 1;
  return 0;
};

const SET_FILTER = {
  filter: 'agSetColumnFilter',
  filterParams: {
    buttons: ['reset'],
    defaultToNothingSelected: false,
  },
};

const StatusCell = (params) => {
  const published = !!(params.data && params.data.has_published);
  return (
    <span
      className={
        'mcc-template-status is-' + (published ? 'published' : 'draft')
      }
    >
      {params.value || '—'}
    </span>
  );
};

const PreviewCell = (params) => {
  const row = params.data;
  if (!row) return null;
  const label =
    (params.t &&
      params.t('MessageControl.Preview', { defaultValue: 'Preview' })) ||
    'Preview';
  return (
    <IconButton
      aria-label={label}
      title={label}
      size="small"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof params.onPreview === 'function') {
          params.onPreview(row);
        }
      }}
    >
      <VisibilityOutlined fontSize="small" />
    </IconButton>
  );
};

const ActionsCell = (params) => {
  const row = params.data;
  if (!row) return null;
  const t = params.t;
  const editLabel =
    (t && t('MessageControl.Edit', { defaultValue: 'Edit' })) || 'Edit';
  const deleteLabel =
    (t && t('MessageControl.Delete', { defaultValue: 'Delete' })) || 'Delete';
  const historyLabel =
    (t &&
      t('MessageControl.AuditHistory', { defaultValue: 'Audit history' })) ||
    'Audit history';
  const makeDefaultLabel =
    (t &&
      t('MessageControl.MakeDefault', { defaultValue: 'Make default' })) ||
    'Make default';
  return (
    <div className="mcc-template-actions-col">
      <IconButton
        aria-label={editLabel}
        title={editLabel}
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof params.onEdit === 'function') {
            params.onEdit(row);
          }
        }}
      >
        <EditOutlined fontSize="small" />
      </IconButton>
      <IconButton
        aria-label={deleteLabel}
        title={deleteLabel}
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof params.onDelete === 'function') {
            params.onDelete(row);
          }
        }}
      >
        <DeleteOutline fontSize="small" />
      </IconButton>
      <IconButton
        aria-label={historyLabel}
        title={historyLabel}
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof params.onHistory === 'function') {
            params.onHistory(row);
          }
        }}
      >
        <HistoryOutlined fontSize="small" />
      </IconButton>
      {row.is_default ? null : (
        <Button
          size="small"
          variant="text"
          disabled={!!params.defaultBusy}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof params.onMakeDefault === 'function') {
              params.onMakeDefault(row);
            }
          }}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0 }}
        >
          {makeDefaultLabel}
        </Button>
      )}
    </div>
  );
};

const TemplateListPage = ({ t, channel: rawChannel }) => {
  const isStaff = rawChannel === 'staff';
  const channel =
    isStaff
      ? 'staff'
      : rawChannel === 'sms'
        ? 'sms'
        : rawChannel === 'mobile'
          ? 'mobile'
          : 'email';
  const isEmail = channel === 'email' || isStaff;
  const isMobile = channel === 'mobile';
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rowsChannel, setRowsChannel] = useState('');
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationsBusy, setLocationsBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBoxId, setCreateBoxId] = useState('');
  const [createMethod, setCreateMethod] = useState('');
  const [createEvent, setCreateEvent] = useState(DEFAULT_EVENT);
  const [createCustomKey, setCreateCustomKey] = useState('');
  const [createCustomLabel, setCreateCustomLabel] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createMethodLocked, setCreateMethodLocked] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [defaultBusy, setDefaultBusy] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const previewTokens = (row) => {
    const loc = (locations || []).find((item) =>
      idsEqual(item.id, row && row.box_id)
    );
    const accountName =
      (row && row.account_name) ||
      (loc && loc.account_name) ||
      'Pharmacy';
    const locationName = (row && row.location_name) || 'Location';
    const messagingName =
      (row && String(row.branding_name || '').trim()) || locationName;
    const logoUrl =
      (row && row.account_logo_url) || (loc && loc.logo_url) || '';
    const expiryLabel = formatExpiryInDays(
      (row && row.expiration_days) || (loc && loc.expiration_days)
    );
    const qrHtml = buildSampleQrHtml(
      parseBrandColors((row && row.html_body) || '').header
    );
    return {
      firstName: 'Maria',
      lastName: 'Garcia',
      fullName: 'Maria Garcia',
      accountName,
      locationName: messagingName,
      locationAddress: '100 Main St, Austin, TX',
      locationPhone: '(512) 555-0100',
      brandPhone: '(512) 555-0100',
      brandEmail: 'pharmacy@example.com',
      pickupCode: '8E8QVW',
      hours: 'Open 24/7',
      link: 'ilocal.link/xxxx',
      amount: row && row.payment_enabled === false ? '' : '$12.40',
      linkTtl: '24 hours',
      expiryDate: expiryLabel,
      website: 'ilocalbox.com',
      qrCode: qrHtml,
      'First Name': 'Maria',
      'Last Name': 'Garcia',
      'Full Name': 'Maria Garcia',
      'Account Name': accountName,
      'Account Logo': accountLogoImgHtml(logoUrl),
      'Location Name': messagingName,
      'Location Address': '100 Main St, Austin, TX',
      'Location Phone': '(512) 555-0100',
      'Brand Phone': '(512) 555-0100',
      'Brand Email': 'pharmacy@example.com',
      'Pickup Code': '8E8QVW',
      Hours: 'Open 24/7',
      'Pharmacy Hours': SAMPLE_PHARMACY_HOURS_HTML,
      'Kiosk Hours': SAMPLE_KIOSK_HOURS_HTML,
      Link: 'ilocal.link/xxxx',
      ...(row && row.payment_enabled === false ? {} : { Amount: '$12.40' }),
      'Expiry Date': expiryLabel,
      Website: 'ilocalbox.com',
      'QR Code': qrHtml,
      'Order Summary':
        channel === 'email'
          ? buildSampleOrderSummaryHtml(parseSummaryConfig(row && row.html_body))
          : buildSampleOrderSummaryText(parseSummaryConfig(row && row.html_body)),
    };
  };

  const openPreview = (row) => {
    setPreviewTarget(row);
    setPreview(null);
    setPreviewBusy(true);
    fetchTemplateHtml(
      row.box_id,
      row.delivery_method,
      isStaff ? row.channel || 'email' : channel,
      row.event_key || DEFAULT_EVENT
    )
      .then((data) => {
        const htmlBody = (data && data.html_body) || '';
        setPreviewTarget({
          ...row,
          html_body: htmlBody,
          link_config: parseMobileConfig(htmlBody, row.delivery_method),
        });
      })
      .catch(() => setPreview(null))
      .finally(() => setPreviewBusy(false));
  };

  const closePreview = () => {
    setPreviewTarget(null);
    setPreview(null);
    setPreviewBusy(false);
  };

  const title = isStaff
    ? t('MessageControl.StaffMail', { defaultValue: 'Staff mail' })
    : isEmail
    ? t('MessageControl.EmailTemplates', { defaultValue: 'Email templates' })
    : isMobile
      ? t('MessageControl.LinkMessages', { defaultValue: 'Link messages' })
      : t('MessageControl.SmsTemplates', { defaultValue: 'SMS templates' });

  const loadRows = async () => {
    const data = await fetchTemplateList(channel);
    setRows((data && data.templates) || []);
    setRowsChannel(channel);
  };

  const loadLocationsIfNeeded = async () => {
    if (locations.length) return locations;
    setLocationsBusy(true);
    try {
      const locationData = await fetchFeatureLocations();
      const next = (locationData && locationData.locations) || [];
      setLocations(next);
      return next;
    } finally {
      setLocationsBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setRowsChannel('');
    setLoading(true);
    setError('');
    setPreviewTarget(null);
    setPreview(null);
    setCreateOpen(false);
    setDeleteTarget(null);
    const boot = async () => {
      try {
        const listData = await fetchTemplateList(channel);
        if (cancelled) return;
        setRows((listData && listData.templates) || []);
        setRowsChannel(channel);
      } catch (err) {
        if (cancelled) return;
        if (err && err.status === 403) {
          navigate(getFeatureLockedRedirectPath(CUSTOM_BRAND_MESSAGING_KEY));
          return;
        }
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [channel]);

  useEffect(() => {
    if (isStaff || !createOpen || !createBoxId || !createMethod) {
      setCreateMethodLocked(false);
      return undefined;
    }
    let cancelled = false;
    fetchMessageOverview(createBoxId)
      .then((data) => {
        if (cancelled) return;
        const products = (data && data.products) || [];
        const match = products.find((product) => product.method === createMethod);
        setCreateMethodLocked(!!(match && match.locked));
      })
      .catch(() => {
        if (!cancelled) setCreateMethodLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [createOpen, createBoxId, createMethod, isStaff]);

  const visibleRows = rowsChannel === channel ? rows : [];
  const showTable = !loading && visibleRows.length > 0;
  const showEmpty = !loading && !visibleRows.length;
  const publishedLabel = t('MessageControl.Published', {
    defaultValue: 'Published',
  });
  const draftLabel = t('MessageControl.Draft', { defaultValue: 'Draft' });

  const gridRows = useMemo(
    () =>
      (visibleRows || []).map((row) => ({
        ...row,
        event_display:
          row.event_label ||
          eventLabel(row.event_key, row.delivery_method),
        status_label: row.has_published ? publishedLabel : draftLabel,
      })),
    [visibleRows, publishedLabel, draftLabel]
  );

  const handleEditRow = (row) => {
    if (!row) return;
    navigate(
      editorPath({
        boxId: row.box_id,
        method: row.delivery_method,
        channel: isStaff ? row.channel || 'email' : channel,
        eventKey: row.event_key || DEFAULT_EVENT,
      })
    );
  };

  const handleMakeDefault = async (row) => {
    if (!row || row.is_default || defaultBusy) return;
    setDefaultBusy(true);
    try {
      await setMessageTemplateDefault({
        box_id: row.box_id,
        method: row.delivery_method,
        channel: isStaff ? row.channel || 'email' : channel,
        event_key: row.event_key || DEFAULT_EVENT,
        is_default: true,
      });
      toast.success(
        t('MessageControl.DefaultSaved', {
          defaultValue:
            'Saved. Orders still use a published template for their location type. This default is used only when that type has none.',
        })
      );
      await loadRows();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDefaultBusy(false);
    }
  };

  const openHistory = async (row) => {
    if (!row) return;
    setHistoryTarget(row);
    setHistoryRows([]);
    setHistoryBusy(true);
    try {
      const data = await fetchTemplateAudit({
        box_id: row.box_id,
        method: row.delivery_method,
        channel: row.channel || (isStaff ? 'email' : channel),
        event_key: row.event_key || DEFAULT_EVENT,
      });
      setHistoryRows((data && data.audit) || []);
    } catch (err) {
      toast.error(err.message);
      setHistoryTarget(null);
    } finally {
      setHistoryBusy(false);
    }
  };

  const defaultColDef = useMemo(() => getDefaultColDef({ minWidth: 120 }), []);

  const columnDefs = useMemo(
    () => {
      const cols = [
        {
          field: 'location_name',
          colId: 'location_name',
          headerName: t('MessageControl.Location', { defaultValue: 'Location' }),
          minWidth: 180,
        },
      ];
      if (!isStaff) {
        cols.push({
          field: 'method_label',
          colId: 'method_label',
          headerName: t('MessageControl.DeliveryMethod', {
            defaultValue: 'Delivery method',
          }),
          ...SET_FILTER,
          minWidth: 150,
        });
      }
      cols.push(
        {
          field: 'event_display',
          colId: 'event_display',
          headerName: t('MessageControl.Event', { defaultValue: 'Event' }),
          ...SET_FILTER,
          minWidth: 170,
        },
        {
          field: 'status_label',
          colId: 'status_label',
          headerName: t('MessageControl.Status', { defaultValue: 'Status' }),
          ...SET_FILTER,
          cellRenderer: StatusCell,
          minWidth: 120,
        }
      );
      if (isEmail) {
        cols.push({
          field: 'email_subject',
          colId: 'email_subject',
          headerName: t('MessageControl.Subject', { defaultValue: 'Subject' }),
          valueGetter: (params) =>
            (params.data && params.data.email_subject) || '—',
          minWidth: 220,
        });
      }
      if (isMobile) {
        cols.push({
          field: 'headline',
          colId: 'headline',
          headerName: t('MessageControl.Headline', { defaultValue: 'Headline' }),
          valueGetter: (params) =>
            (params.data && params.data.headline) || '—',
          minWidth: 180,
        });
      }
      cols.push(
        {
          field: 'preview',
          colId: 'preview',
          headerName: t('MessageControl.Preview', { defaultValue: 'Preview' }),
          cellRenderer: PreviewCell,
          cellRendererParams: { t, onPreview: openPreview },
          filter: false,
          sortable: false,
          suppressMenu: true,
          minWidth: 96,
          maxWidth: 120,
          flex: 0,
        },
        {
          field: 'updated_at',
          colId: 'updated_at',
          headerName: t('MessageControl.Updated', { defaultValue: 'Updated' }),
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: compareUpdatedDay,
            buttons: ['reset'],
            browserDatePicker: true,
          },
          valueFormatter: (params) => formatWhen(params.value),
          minWidth: 180,
        },
        {
          field: 'created_by_label',
          colId: 'created_by_label',
          headerName: t('MessageControl.CreatedBy', {
            defaultValue: 'Created by',
          }),
          valueGetter: (params) =>
            (params.data && params.data.created_by_label) || '—',
          minWidth: 150,
        },
        {
          field: 'updated_by_label',
          colId: 'updated_by_label',
          headerName: t('MessageControl.ModifiedBy', {
            defaultValue: 'Modified by',
          }),
          valueGetter: (params) =>
            (params.data && params.data.updated_by_label) || '—',
          minWidth: 150,
        },
        {
          field: 'actions',
          colId: 'actions',
          headerName: t('MessageControl.Actions', { defaultValue: 'Actions' }),
          cellRenderer: ActionsCell,
          cellRendererParams: {
            t,
            onEdit: handleEditRow,
            onDelete: setDeleteTarget,
            onHistory: openHistory,
            onMakeDefault: handleMakeDefault,
            defaultBusy,
          },
          filter: false,
          sortable: false,
          suppressMenu: true,
          minWidth: 220,
          maxWidth: 280,
          flex: 0,
          pinned: 'right',
          cellClass: 'ilocal-ag-actions-col',
        }
      );
      return cols;
    },
    [t, isEmail, isMobile, isStaff, defaultBusy, handleMakeDefault]
  );

  const locationItems = useMemo(
    () =>
      (locations || []).map((location) => {
        const method = methodForLocation(location, isStaff);
        const typeLabel =
          !isStaff && method && METHOD_LABELS[method]
            ? METHOD_LABELS[method]
            : '';
        return {
          id: location.id,
          name: typeLabel ? `${location.name} (${typeLabel})` : location.name,
        };
      }),
    [locations, isStaff]
  );

  const createEventItems = useMemo(() => {
    if (!createMethod) return [];
    const taken = takenEventKeysForLocation({
      rows: visibleRows,
      boxId: createBoxId,
      method: createMethod,
      channel,
      isStaffChannel: isStaff,
    });
    const items = eventsForMethod(createMethod, channel)
      .filter((event) => !taken.has(event.key))
      .map((event) => ({
        id: event.key,
        name: event.label,
      }));
    if (!isMobile) {
      items.push({
        id: CUSTOM_EVENT_VALUE,
        name: t('MessageControl.CustomEvent', {
          defaultValue: 'Custom event…',
        }),
      });
    }
    return items;
  }, [createMethod, createBoxId, visibleRows, channel, isStaff, isMobile, t]);

  const applyCreateLocation = (boxId, locationList) => {
    const loc = (locationList || []).find((item) => item.id === boxId);
    const nextMethod = methodForLocation(loc, isStaff);
    const taken = takenEventKeysForLocation({
      rows: visibleRows,
      boxId,
      method: nextMethod,
      channel,
      isStaffChannel: isStaff,
    });
    setCreateBoxId(boxId || '');
    setCreateMethod(nextMethod);
    setCreateEvent(firstEventFor(nextMethod, channel, taken));
    setCreateCustomKey('');
    setCreateCustomLabel('');
  };

  const openCreate = () => {
    setCreateCustomKey('');
    setCreateCustomLabel('');
    setCreateOpen(true);
    setError('');
    const firstId = (locations[0] && locations[0].id) || '';
    if (firstId) {
      applyCreateLocation(firstId, locations);
      return;
    }
    setCreateBoxId('');
    setCreateMethod(isStaff ? 'account' : '');
    setCreateEvent(isStaff ? 'welcome' : DEFAULT_EVENT);
    loadLocationsIfNeeded().then((next) => {
      if (next && next[0]) applyCreateLocation(next[0].id, next);
    });
  };

  const submitCreate = async () => {
    const isCustom = createEvent === CUSTOM_EVENT_VALUE;
    const eventKey = isCustom
      ? slugifyEvent(createCustomKey || createCustomLabel)
      : createEvent;
    if (!createBoxId || !createMethod || !eventKey) {
      setError(
        t('MessageControl.CreateRequired', {
          defaultValue: isStaff
            ? 'Choose a location and event — or enter a custom event key.'
            : 'Choose a location and event — events follow that location’s type.',
        })
      );
      return;
    }
    if (!isValidEventKey(eventKey)) {
      setError(
        t('MessageControl.InvalidEventKey', {
          defaultValue:
            'Use a key like holiday_hours: start with a letter, then letters, numbers, or underscores.',
        })
      );
      return;
    }
    const taken = takenEventKeysForLocation({
      rows: visibleRows,
      boxId: createBoxId,
      method: createMethod,
      channel,
      isStaffChannel: isStaff,
    });
    if (taken.has(eventKey)) {
      setError(
        t('MessageControl.TemplateExists', {
          defaultValue: 'This location already has a template for that event.',
        })
      );
      return;
    }
    if (createMethodLocked) {
      setError(
        t('MessageControl.LicenseRequired', {
          defaultValue: 'License required',
        })
      );
      return;
    }
    setCreateBusy(true);
    setError('');
    const createChannel = isStaff
      ? createEvent === 'otp'
        ? 'email'
        : 'email'
      : channel;
    try {
      await createListedTemplate({
        box_id: createBoxId,
        method: createMethod,
        channel: createChannel,
        event_key: eventKey,
      });
      setCreateOpen(false);
      navigate(
        editorPath({
          boxId: createBoxId,
          method: createMethod,
          channel: createChannel,
          eventKey,
        })
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setCreateBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteListedTemplate({
        box_id: deleteTarget.box_id,
        method: deleteTarget.delivery_method,
        channel: isStaff ? deleteTarget.channel || 'email' : channel,
        event_key: deleteTarget.event_key || DEFAULT_EVENT,
      });
      toast.success(
        t('MessageControl.Deleted', {
          defaultValue: 'Template deleted.',
        })
      );
      setDeleteTarget(null);
      await loadRows();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="mcc-page mcc-template-list">
      <div className="mcc-hero">
        <span className="mcc-hero-icon">
          {isStaff ? (
            <BadgeOutlined />
          ) : isEmail ? (
            <EmailOutlined />
          ) : isMobile ? (
            <LinkOutlined />
          ) : (
            <SmsOutlined />
          )}
        </span>
        <div className="mcc-hero-copy">
          <p className="mcc-hero-eyebrow">
            {t('MessageControl.ProductName', {
              defaultValue: 'Studio',
            })}
          </p>
          <h1>{title}</h1>
          <p className="mcc-helper">
            {t('MessageControl.TemplateListHelper', {
              defaultValue: isStaff
                ? 'Welcome, password, OTP, and kiosk-offline mail for staff. Publish here to overlay those send paths.'
                : isMobile
                ? 'Create, edit, or delete the page patients see after they tap the SMS link.'
                : 'Create, edit, or delete templates for each location and notification event. Events follow the location’s type.',
            })}
          </p>
        </div>
        <div className="mcc-hero-actions">
          <MessageWhenHelpButton
            t={t}
            channel={isStaff ? 'staff' : channel}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreate}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {t('MessageControl.NewTemplate', { defaultValue: 'New template' })}
          </Button>
        </div>
      </div>

      <Tabs
        value={channel}
        onChange={(_event, next) => {
          if (next === channel) return;
          const paths = {
            email: EMAIL_TEMPLATES_PATH,
            sms: SMS_TEMPLATES_PATH,
            mobile: LINK_MESSAGES_PATH,
            staff: STAFF_TEMPLATES_PATH,
          };
          if (paths[next]) {
            navigate(paths[next]);
          }
        }}
        className="mcc-channel-tabs"
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ className: 'mcc-channel-tabs-indicator' }}
      >
        <Tab
          className="mcc-channel-tab"
          value="email"
          icon={<EmailOutlined fontSize="small" />}
          iconPosition="start"
          label={t('Leftbar.EmailTemplates', { defaultValue: 'Email templates' })}
        />
        <Tab
          className="mcc-channel-tab"
          value="sms"
          icon={<SmsOutlined fontSize="small" />}
          iconPosition="start"
          label={t('Leftbar.SmsTemplates', { defaultValue: 'SMS templates' })}
        />
        <Tab
          className="mcc-channel-tab"
          value="mobile"
          icon={<LinkOutlined fontSize="small" />}
          iconPosition="start"
          label={t('Leftbar.LinkTemplates', { defaultValue: 'Link templates' })}
        />
        <Tab
          className="mcc-channel-tab"
          value="staff"
          icon={<BadgeOutlined fontSize="small" />}
          iconPosition="start"
          label={t('Leftbar.StaffMail', { defaultValue: 'Staff mail' })}
        />
      </Tabs>

      {loading ? (
        <div className="mcc-template-grid-wrap">
          <LinearProgress />
        </div>
      ) : null}
      {error && !createOpen ? <p className="mcc-error">{error}</p> : null}

      {showEmpty ? (
        <div className="mcc-template-empty">
          <FeatureGlyph featureKey={CUSTOM_BRAND_MESSAGING_KEY} />
          <p>
            {t('MessageControl.NoTemplates', {
              defaultValue: 'No templates yet. Create one to start customizing copy.',
            })}
          </p>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={openCreate}
            sx={{ textTransform: 'none' }}
          >
            {t('MessageControl.NewTemplate', { defaultValue: 'New template' })}
          </Button>
        </div>
      ) : null}

      {showTable ? (
        <div className="mcc-template-grid-wrap">
          <ILocalAGGrid
            className="ag-theme-alpine mcc-template-grid"
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="clientSide"
            rowData={gridRows}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={48}
            overlayNoRowsMessage={t('MessageControl.NoFilterMatches', {
              defaultValue: 'No templates match these filters.',
            })}
            getRowId={(params) => {
              const row = params.data || {};
              return [
                row.box_id,
                row.delivery_method,
                row.event_key || DEFAULT_EVENT,
              ].join(':');
            }}
          />
        </div>
      ) : null}

      <Dialog
        open={createOpen}
        onClose={() => {
          if (!createBusy) setCreateOpen(false);
        }}
        className="mcc-confirm-dialog"
        PaperProps={{ className: 'mcc-confirm-paper mcc-create-paper' }}
      >
        <DialogTitle className="mcc-confirm-title">
          {t('MessageControl.NewTemplate', { defaultValue: 'New template' })}
          <span className="mcc-create-title-tools">
            <MessageWhenHelpButton
              t={t}
              method={isStaff ? 'account' : createMethod}
              channel={isStaff ? 'staff' : channel}
              eventKey={
                createEvent && createEvent !== CUSTOM_EVENT_VALUE
                  ? createEvent
                  : ''
              }
              size="small"
            />
            <IconButton
              aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
              onClick={() => setCreateOpen(false)}
              disabled={createBusy}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </span>
        </DialogTitle>
        <DialogContent>
          <p className="mcc-helper">
            {t('MessageControl.NewTemplateHelp', {
              defaultValue: isStaff
                ? 'Choose a location, then pick a staff mail event — or add a custom event key.'
                : 'Choose a location. Events follow that location’s type (Counter, Kiosk, Curbside, and the rest). Pick an existing notification, or add a custom event key.',
            })}
          </p>
          <div className="mcc-create-fields">
            <SearchableSelect
              id="mcc-create-location"
              label={t('MessageControl.Location', { defaultValue: 'Location' })}
              items={locationItems}
              value={createBoxId || ''}
              placeholder={t('MessageControl.SearchLocations', {
                defaultValue: 'Search locations...',
              })}
              variant="outlined"
              sx={{ minWidth: 0 }}
              onChange={(event) =>
                applyCreateLocation(event.target.value, locations)
              }
            />
            {locationsBusy ? (
              <LinearProgress className="mcc-dialog-progress" />
            ) : null}
            {!isStaff && createBoxId && !createMethod ? (
              <p className="mcc-error">
                {t('MessageControl.UnsupportedLocationType', {
                  defaultValue:
                    'This location’s type is not supported for templates.',
                })}
              </p>
            ) : null}
            {createMethodLocked ? (
              <p className="mcc-error">
                {t('MessageControl.LicenseRequired', {
                  defaultValue: 'License required',
                })}
              </p>
            ) : null}
            <SearchableSelect
              id="mcc-create-event"
              label={t('MessageControl.Event', { defaultValue: 'Event' })}
              items={createEventItems}
              value={createEvent || ''}
              placeholder={t('MessageControl.SearchEvents', {
                defaultValue: 'Search events...',
              })}
              variant="outlined"
              sx={{ minWidth: 0 }}
              disabled={!createMethod}
              onChange={(event) => setCreateEvent(event.target.value)}
            />
            {createMethod &&
            !createEventItems.some((item) => item.id !== CUSTOM_EVENT_VALUE) ? (
              <p className="mcc-helper">
                {t('MessageControl.AllEventsUsed', {
                  defaultValue: isMobile
                    ? 'This location already has a template for this event.'
                    : 'This location already has a template for every standard event. Add a custom event if you need another.',
                })}
              </p>
            ) : null}
            {createEvent &&
            createEvent !== CUSTOM_EVENT_VALUE &&
            eventWhen(createEvent) ? (
              <p className="mcc-helper">
                {t(`MessageControlHelp.When.${createEvent}.Body`, {
                  defaultValue: eventWhen(createEvent),
                })}
              </p>
            ) : null}
            {createEvent === CUSTOM_EVENT_VALUE ? (
              <React.Fragment>
                <TextField
                  label={t('MessageControl.CustomEventName', {
                    defaultValue: 'Event name',
                  })}
                  value={createCustomLabel}
                  onChange={(event) => {
                    const nextLabel = event.target.value;
                    const previousSlug = slugifyEvent(createCustomLabel);
                    setCreateCustomLabel(nextLabel);
                    if (!createCustomKey || createCustomKey === previousSlug) {
                      setCreateCustomKey(slugifyEvent(nextLabel));
                    }
                  }}
                  placeholder={t('MessageControl.CustomEventNameHint', {
                    defaultValue: 'Holiday hours',
                  })}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <TextField
                  label={t('MessageControl.CustomEventKey', {
                    defaultValue: 'Event key',
                  })}
                  value={createCustomKey}
                  onChange={(event) =>
                    setCreateCustomKey(slugifyEvent(event.target.value))
                  }
                  placeholder={t('MessageControl.CustomEventKeyHint', {
                    defaultValue: 'holiday_hours',
                  })}
                  helperText={t('MessageControl.CustomEventKeyHelp', {
                    defaultValue:
                      'This key is saved on the template. Use it when you want this copy to send for a matching notification.',
                  })}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </React.Fragment>
            ) : null}
          </div>
          {error && createOpen ? <p className="mcc-error">{error}</p> : null}
        </DialogContent>
        <DialogActions className="mcc-confirm-actions">
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createBusy}
            variant="outlined"
          >
            {t('MessageControl.Cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={submitCreate}
            disabled={
              createBusy ||
              createMethodLocked ||
              !createBoxId ||
              !createMethod ||
              !createEvent
            }
            variant="contained"
            startIcon={
              createBusy ? <CircularProgress size={16} color="inherit" /> : <Add />
            }
          >
            {createBusy
              ? t('MessageControl.BusyCreate', {
                  defaultValue: 'Creating…',
                })
              : t('MessageControl.CreateAndEdit', {
                  defaultValue: 'Create and edit',
                })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!previewTarget}
        onClose={closePreview}
        fullScreen
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        PaperProps={{ className: 'mcc-dialog-paper' }}
      >
        <div className="mcc-dialog-shell mcc-list-preview-shell">
          {previewBusy ? (
            <LinearProgress className="mcc-dialog-progress" />
          ) : null}
          <DialogTitle className="mcc-dialog-header">
            <span className="mcc-dialog-title-text">
              {t('MessageControl.Preview', { defaultValue: 'Preview' })}  
              {previewTarget
                ? ` — ${previewTarget.method_label} — ${
                    previewTarget.event_label ||
                    eventLabel(
                      previewTarget.event_key,
                      previewTarget.delivery_method
                    )
                  } — ${previewTarget.location_name}`
                : ''}
            </span>
            <IconButton
              aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
              onClick={closePreview}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent className="mcc-list-preview-body">
            {previewTarget ? (
              <MessagePreview
                channel={isStaff ? previewTarget.channel || 'email' : channel}
                preview={preview}
                previewBusy={previewBusy}
                html={
                  previewTarget.payment_enabled === false
                    ? stripPaymentContent(
                        (preview && preview.html) ||
                          previewTarget.html_body ||
                          ''
                      )
                    : (preview && preview.html) || previewTarget.html_body || ''
                }
                subject={previewTarget.email_subject || ''}
                headline={previewTarget.headline || ''}
                intro={previewTarget.intro || ''}
                ctaLabel={previewTarget.cta_label || ''}
                greeting={
                  (previewTarget.link_config &&
                    previewTarget.link_config.greeting) ||
                  ''
                }
                customMessage={
                  (previewTarget.link_config &&
                    previewTarget.link_config.custom_message) ||
                  ''
                }
                arrivalLabel={
                  (previewTarget.link_config &&
                    previewTarget.link_config.arrival_label) ||
                  ''
                }
                buttonColor={
                  previewTarget.link_config &&
                  previewTarget.link_config.button_color
                }
                accentColor={
                  previewTarget.link_config &&
                  previewTarget.link_config.accent_color
                }
                hideAmountDue={
                  previewTarget.payment_enabled === false ||
                  !!(
                    previewTarget.link_config &&
                    previewTarget.link_config.hide_amount
                  )
                }
                paymentEnabled={previewTarget.payment_enabled !== false}
                logoUrl={
                  (previewTarget.link_config &&
                    previewTarget.link_config.logo_url) ||
                  ''
                }
                accountName={
                  (previewTarget && previewTarget.account_name) ||
                  ((locations || []).find((item) =>
                    idsEqual(item.id, previewTarget && previewTarget.box_id)
                  ) || {}).account_name ||
                  'Pharmacy'
                }
                brandColors={parseBrandColors(
                  (preview && preview.html) || previewTarget.html_body || ''
                )}
                locationName={previewTarget.location_name}
                methodLabel={previewTarget.method_label}
                tokens={previewTokens(previewTarget)}
                t={t}
              />
            ) : null}
          </DialogContent>
        </div>
      </Dialog>

      <ThemedConfirmDialog
        open={!!deleteTarget}
        title={t('MessageControl.DeleteTemplateTitle', {
          defaultValue: 'Delete template',
        })}
        message={
          deleteTarget
            ? t('MessageControl.DeleteTemplateConfirm', {
                defaultValue:
                  'Delete the {{channel}} template for {{event}} ({{method}}) at {{location}}? This removes the draft and published copies.',
                channel: isEmail ? 'email' : isMobile ? 'link' : 'SMS',
                event:
                  deleteTarget.event_label ||
                  eventLabel(
                    deleteTarget.event_key,
                    deleteTarget.delivery_method
                  ),
                method: deleteTarget.method_label,
                location: deleteTarget.location_name,
              })
            : ''
        }
        cancelLabel={t('MessageControl.Cancel', { defaultValue: 'Cancel' })}
        confirmLabel={t('MessageControl.Delete', { defaultValue: 'Delete' })}
        confirmBusyLabel={t('MessageControl.BusyDelete', {
          defaultValue: 'Deleting…',
        })}
        busy={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
      />
      <AuditHistoryDialog
        t={t}
        open={!!historyTarget}
        title={
          historyTarget
            ? t('MessageControl.AuditTitle', {
                defaultValue: 'Audit history — {{event}}',
                event:
                  historyTarget.event_label ||
                  eventLabel(
                    historyTarget.event_key,
                    historyTarget.delivery_method
                  ),
              })
            : t('MessageControl.AuditHistory', {
                defaultValue: 'Audit history',
              })
        }
        rows={historyRows}
        loading={historyBusy}
        empty={t('MessageControl.AuditEmpty', {
          defaultValue: 'No audit entries yet for this template.',
        })}
        onClose={() => {
          setHistoryTarget(null);
          setHistoryRows([]);
        }}
      />
    </div>
  );
};

export default withTranslation()(TemplateListPage);



// WEBPACK FOOTER //
// ./src/components/message-control-center/TemplateList.js